import { Field, FieldProps, FormikHelpers, FormikProps } from "formik";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import React, { useCallback } from "react";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  Skeleton,
  Spacer,
} from "@chakra-ui/react";
import { toTitle } from "@/utils/case";
import AppInput from "@/features/ui/app-input";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppRemittanceStatusLabel from "@/features/ui/app-remittance-status-label";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import CreateCompanyModal, {
  CreateCompanyModalProps,
} from "@/features/ui/create-company-modal";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppInvoiceStatusLabel from "@/features/ui/app-invoice-status-label";
import AppInvoiceStatusSelect from "@/features/invoices/order-confirmation-invoices/app-invoice-status-select";
import { ProformaInvoiceFormFieldsModal } from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page-modal";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppPriceInput from "@/features/invoices/app-price-input";

interface ProformaInvoiceInformationFormControlProps {
  formik: FormikProps<FormFields>;
  isEditing?: boolean;
  isDisabled?: boolean;
  // 우노 수수료가 "송금 필요" 상태이면 우노 수수료 환율을 수정 가능하게 한다.
  isUnoFeeRemittanceRequired?: boolean;
}

interface FormFields extends ProformaInvoiceFormFieldsModal {}

export default function ProformaInvoiceInformationFormControlModal({
  formik,
  isEditing,
  isDisabled,
  isUnoFeeRemittanceRequired = false,
}: ProformaInvoiceInformationFormControlProps) {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();

  // 3자무역을 켜면 발행사를 3자무역 회사로 바꾸고 원래 부티크는 원발행사로 남긴다(연결 오더·크레딧은 원발행사 기준이라 그대로).
  // 끄면 원발행사(부티크)를 다시 발행사로 돌리고 수수료율을 비운다.
  const handleCrossTradeChange = useCallback(
    (checked: boolean) => {
      formik.setValues((prev) => {
        const boutique = prev.originallyIssuedBy || prev.issuedBy;
        if (checked) {
          return {
            ...prev,
            isCrossTrade: true,
            originallyIssuedBy: boutique,
          };
        }
        return {
          ...prev,
          isCrossTrade: false,
          issuedBy: boutique,
          originallyIssuedBy: undefined,
          crossTradeFeeRate: undefined,
        };
      });
    },
    [formik]
  );
  const crossTrader =
    formik.values.issuedBy?.type === "CROSS_TRADER"
      ? formik.values.issuedBy
      : undefined;

  type LINK_AFFECTING_KEYS =
    | "issuedBy"
    | "orderedBy"
    | "originallyIssuedBy"
    | "issuedThrough"
    | "paymentMethod"
    | "paidByAgency";

  const handleLinkAffectingFieldChange = useCallback(
    <T extends LINK_AFFECTING_KEYS>(
      key: LINK_AFFECTING_KEYS,
      value: FormFields[T],
      setValues: FormikHelpers<FormFields>["setValues"]
    ) => {
      return setValues((prev) => {
        let next = {
          ...prev,
          [key]: value,
          //orderSheetSets: [],
          //orderConfirmationInvoices: [],
        };

        if (
          key === "issuedBy" &&
          value &&
          (value as FormFields["issuedBy"])?.type === "BOUTIQUE" &&
          next.originallyIssuedBy === undefined
        ) {
          // @ts-ignore
          next.originallyIssuedBy = { ...value };
        } else if (
          key === "originallyIssuedBy" &&
          value &&
          next.orderedBy === undefined
        ) {
          // @ts-ignore
          next.orderedBy = { ...value };
        }

        const prevIssuedBy = prev.originallyIssuedBy || prev.issuedBy;
        const nextIssuedBy = next.originallyIssuedBy || next.issuedBy;

        if (key === "paymentMethod" && value === "LOAN") {
          next.paidByAgency = true;
        }

        if (
          !(
            prev.orderedBy?.id === next.orderedBy?.id &&
            prev.issuedThrough?.id === next.issuedThrough?.id &&
            prevIssuedBy?.id === nextIssuedBy?.id &&
            prev.paidByAgency === next.paidByAgency
          )
        ) {
          next.orderSheetSets = [];
          next.orderConfirmationInvoices = [];
          next.childProformaInvoices = [];
          next.proformaInvoiceDetailList = next.proformaInvoiceDetailList.map(
            (pDetail) => {
              return {
                ...pDetail,
                orderSheetSet: undefined,
                orderConfirmationInvoiceDetail: undefined,
              };
            }
          );
        }

        // next.orderConfirmationInvoiceDetailList =
        //   next.orderConfirmationInvoiceDetailList.map((ocDetail) => {
        //     return {
        //       ...ocDetail,
        //       brand: undefined,
        //       orderSheetSet: undefined,
        //     };
        //   });

        return next;
      });
    },
    []
  );

  const {
    isOpen: isCreateCompanyModelOpen,
    onClose: onCreateCompanyModalClose,
    data: createCompanyModalData,
    openWithData: openCreateCompanyModal,
  } = useDisclosureWithData<CreateCompanyModalProps>();

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          rowGap={"8px"}
          gridTemplateRows={"repeat(7, 16px 24px) repeat(2, 16px 60px)"}
        >
          {(Array(18).fill(0) as number[]).map((_, index) => {
            return <Skeleton key={index} />;
          })}
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("common:invoice_information.abbr")}>
      <Grid rowGap={"8px"}>
        <Field name={"status"}>
          {(props: FieldProps<FormFields["status"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={isEditing ? "column" : "row"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("status"))}
                </FormLabel>
                {isEditing ? (
                  <AppInvoiceStatusSelect
                    value={props.field.value}
                    isReadOnly={isDisabled}
                    onSelect={(value) => {
                      props.form.setFieldValue("status", value);
                    }}
                  />
                ) : (
                  <>
                    <Spacer />
                    <AppInvoiceStatusLabel value={props.field.value} />
                  </>
                )}
              </FormControl>
            );
          }}
        </Field>

        {!isEditing && (
          <>
            <Field name={"isRemittedToBoutique"}>
              {(
                props: FieldProps<
                  FormFields["isRemittedToBoutique"],
                  FormFields
                >
              ) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"row"}
                    gap={"4px"}
                    isRequired={isEditing}
                    fontSize={"12px"}
                  >
                    <FormLabel
                      fontSize={"12px"}
                      color={"#6F6F6C"}
                      padding={"0"}
                      margin={"0"}
                    >
                      {toTitle(t("remittance_status_to_boutique"))}
                    </FormLabel>
                    <Spacer />
                    <AppRemittanceStatusLabel
                      value={
                        props.field.value !== undefined
                          ? props.field.value
                            ? "REMITTED"
                            : "UNREMITTED"
                          : undefined
                      }
                    />
                  </FormControl>
                );
              }}
            </Field>

            {formik.values.paidByAgency && (
              <Field name={"isRemittedToAgency"}>
                {(
                  props: FieldProps<
                    FormFields["isRemittedToAgency"],
                    FormFields
                  >
                ) => {
                  return (
                    <FormControl
                      display={"flex"}
                      flexDirection={"row"}
                      gap={"4px"}
                      isRequired={isEditing}
                      fontSize={"12px"}
                    >
                      <FormLabel
                        fontSize={"12px"}
                        color={"#6F6F6C"}
                        padding={"0"}
                        margin={"0"}
                      >
                        {toTitle(t("common:remittance_status_to_agency"))}
                      </FormLabel>
                      <Spacer />
                      <AppRemittanceStatusLabel
                        value={
                          props.field.value !== undefined
                            ? props.field.value
                              ? "REMITTED"
                              : "UNREMITTED"
                            : undefined
                        }
                      />
                    </FormControl>
                  );
                }}
              </Field>
            )}

            <Box
              height={"8px"}
              borderBottom={"0.5px solid var(--Pathrade-Gray-Groomy, #8F8F8C)"}
            ></Box>
          </>
        )}

        <Field name={"name"}>
          {(props: FieldProps<FormFields["name"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("title"))}
                </FormLabel>
                <AppInput
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"number"}>
          {(props: FieldProps<FormFields["number"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("number"))}
                </FormLabel>
                <AppInput
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"issuedOn"}>
          {(props: FieldProps<Date, FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                isRequired={isEditing}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("issued_on"))}
                </FormLabel>
                <AppDatePicker
                  width={"100%"}
                  showTimeInput={false}
                  name={tTitle("issued_on")}
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  // 발행일자는 전체 잠금(remitted/확정 등) 여부와 무관하게 항상 수정 가능.
                  isDisabled={false}
                  onChange={(value) => {
                    props.form.setFieldValue("issuedOn", value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {identity?.company?.type === "AGENCY" && (
          <Field name={"exposedToBuyer"}>
            {(props: FieldProps<FormFields["exposedToBuyer"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                >
                  <AppCheckBox
                    isChecked={props.field.value}
                    isDisabled={!isEditing}
                    name={toTitle(t("exposed_to_buyer"))}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        <Field name={"unoFeeRate"}>
          {(props: FieldProps<FormFields["unoFeeRate"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("common:uno_fee_rate"))}
                </FormLabel>
                <AppInput
                  isReadOnly={!isEditing}
                  isDisabled={isDisabled}
                  rightText={"%"}
                  value={
                    props.field.value !== undefined &&
                    props.field.value !== null
                      ? String(props.field.value)
                      : ""
                  }
                  onChange={(value) => {
                    if (value === undefined || value === "") {
                      props.form.setFieldValue(props.field.name, undefined);
                      return;
                    }
                    const parsed = parseInt(value, 10);
                    if (Number.isNaN(parsed)) {
                      return;
                    }
                    props.form.setFieldValue(props.field.name, parsed);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"unoFeeExchangeRate"}>
          {(
            props: FieldProps<FormFields["unoFeeExchangeRate"], FormFields>
          ) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  우노 수수료 환율
                </FormLabel>
                <AppInput
                  isReadOnly={!isEditing}
                  isDisabled={isDisabled && !isUnoFeeRemittanceRequired}
                  value={
                    props.field.value !== undefined &&
                    props.field.value !== null
                      ? String(props.field.value)
                      : ""
                  }
                  onChange={(value) => {
                    if (value === undefined || value === "") {
                      props.form.setFieldValue(props.field.name, undefined);
                      return;
                    }
                    const parsed = parseFloat(value);
                    if (Number.isNaN(parsed)) {
                      return;
                    }
                    props.form.setFieldValue(props.field.name, parsed);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {/* 3자무역 — 고른 회사가 발행사(issued_by_company)가 되고, 수수료는 송금 필요 금액에 곱한다. */}
        {identity?.company?.type !== "BOUTIQUE" && (
          <FormControl display={"flex"} flexDirection={"column"} gap={"4px"}>
            <AppCheckBox
              isChecked={!!formik.values.isCrossTrade}
              isDisabled={!isEditing || isDisabled}
              name={"3자무역"}
              onChange={handleCrossTradeChange}
            />
          </FormControl>
        )}
        {identity?.company?.type !== "BOUTIQUE" && formik.values.isCrossTrade && (
          <>
            <FormControl
              display={"flex"}
              flexDirection={"column"}
              gap={"4px"}
              isRequired={isEditing}
              fontSize={"12px"}
            >
              <FormLabel
                fontSize={"12px"}
                color={"#6F6F6C"}
                padding={"0"}
                margin={"0"}
              >
                3자무역 회사
              </FormLabel>
              <AppCompanySelect
                name={"3자무역 회사"}
                types={["CROSS_TRADER"]}
                width={"100%"}
                id={crossTrader?.id}
                isDisabled={isDisabled}
                isReadOnly={!isEditing}
                onSelect={(value) => {
                  formik.setValues((prev) => {
                    const boutique = prev.originallyIssuedBy || prev.issuedBy;
                    return {
                      ...prev,
                      originallyIssuedBy: boutique,
                      // 고르면 프로포마 발행사(issued_by_company)로 저장된다
                      issuedBy: value ?? boutique,
                    };
                  });
                }}
              />
            </FormControl>
            {/* 3자무역 수수료율은 더 이상 입력하지 않는다 — 인보이스 총액을 그대로 넣는다(2026-10-30) */}
          </>
        )}
      </Grid>
      {isCreateCompanyModelOpen && createCompanyModalData && (
        <CreateCompanyModal
          isOpen={isCreateCompanyModelOpen}
          onClose={onCreateCompanyModalClose}
          {...createCompanyModalData}
        />
      )}
    </AppPanel>
  );
}
